import {Button, MenuGroup, MenuItem, NavigableMenu, Notice, PanelBody, Popover, Spinner, TextControl, TextHighlight} from '@wordpress/components';
import {__, sprintf} from '@wordpress/i18n';
import type {Type} from '@wordpress/api/types';
import type {Attr as PageAttr} from './pages/Edit';
import type {Attr as CategoryAttr} from './categories/Edit';
import {type BlockEditProps} from '@wordpress/blocks';
import type {Taxonomy} from '@wordpress/api/taxonomies';
import classNames from 'classnames';
import useDebouncedInput from '../../hooks/useDebouncedInput';
import useEffectOnChange from '../../hooks/useEffectOnChange';
import {fetchItems} from '../../api/rest/search';
import type {SearchItem} from '@wordpress/api/search';
import {Dispatch, SetStateAction, useCallback, useEffect, useRef, useState} from 'react';
import {decodeEntities} from '@wordpress/html-entities';

import styles from './exclude-field.pcss';

type Props = {
	attributes: PageAttr | CategoryAttr;
	setAttributes: BlockEditProps<PageAttr | CategoryAttr>['setAttributes'];
	type?: Type<'edit'> | Taxonomy<'edit'>;
	block: 'advanced-sidebar-menu/pages' | 'advanced-sidebar-menu/categories';
};

/**
 * Get the IDs from the exclude value.
 */
function getIdsFromValue( exclude: string ): number[] {
	if ( '' === exclude ) {
		return [];
	}

	return exclude.split( ',' ).map( i => parseInt( i, 10 ) );
}


/**
 * The PRO Exclude field shared between widgets.
 *
 * Replaces the exclude field in the basic version.
 *
 * 1. Remove basic exclude field via a filter.
 * 2. Include this component in the PRO Edit.tsx.
 */
const ExcludeField = ( {type, attributes, setAttributes, block}: Props ) => {
	const [ search, setSearch, debouncedSearchTerm ] = useDebouncedInput( '' );
	const [ items, setItems ] = useState<SearchItem[]>( [] );
	const [ selected, setSelected ] = useState<SearchItem[]>( [] );
	const [ loading, setLoading ] = useState<boolean>( false );
	const [ error, setError ] = useState<string>( '' );

	const inputRef = useRef<HTMLInputElement>( null );
	const firstItemRef = useRef<HTMLButtonElement>( null );

	const clearItems = useCallback( () => {
		setItems( [] );
	}, [ setItems ] );

	/**
	 * Search for items when the search term changes.
	 */
	useEffectOnChange( () => {
		setError( '' );
		if ( debouncedSearchTerm.length >= 2 ) {
			searchItems( setItems );
		} else {
			clearItems();
		}
	}, [ debouncedSearchTerm ] );


	/**
	 * Preload the selected items.
	 */
	useEffect( () => {
		const ids = getIdsFromValue( attributes.exclude ?? '' );
		if ( ids.length > 0 ) {
			searchItems( setSelected, getIdsFromValue( attributes.exclude ?? '' ) );
		} else {
			setSelected( [] );
		}
		// eslint-disable-next-line -- We don't want this to fire when the attributes change.
	}, [ setSelected, type ] );

	const isPage = 'advanced-sidebar-menu/pages' === block;

	/**
	 * Search for items using the REST API.
	 */
	const searchItems = ( setCallback: Dispatch<SetStateAction<SearchItem[]>>, include: number[] = [] ) => {
		const itemType = isPage ? 'post' : 'term';
		let subType = isPage ? 'page' : 'category';
		if ( undefined !== type ) {
			subType = type.slug;
		}
		const exclude = selected.map( i => parseInt( i.id.toString() ) );
		setLoading( true );
		fetchItems( debouncedSearchTerm, itemType, subType, include, exclude )
			.then( results => {
				setCallback( results.filter( item => {
					// Limit results to the currently selected post_type/taxonomy.
					if ( 'undefined' === typeof item.subtype ) {
						return item.type === subType;
					}
					return item.subtype === subType;
				} ) );
			} )
			.catch( ( err: Error ) => {
				setError( err.message );
			} )
			.finally( () => setLoading( false ) );
	};


	/**
	 * Select an item from the search results.
	 */
	const selectItem = ( item: SearchItem ) => {
		const existing = getIdsFromValue( attributes.exclude ?? '' );
		existing.push( parseInt( item.id.toString() ) );
		setAttributes( {
			exclude: existing.join( ',' ),
		} );
		clearItems();
		setSearch( '' );
		setSelected( [ ...selected, item ] );
	};

	// noinspection SpellCheckingInspection
	const help = <span
		dangerouslySetInnerHTML={{ //phpcs:ignore
			__html: sprintf(
				/* translators: {post type plural label}, {post type single label}, {<a>}, {</a>} */
				__( '%1$s may also be excluded from all menus via the Advanced Sidebar %3$ssettings%4$s when editing a %2$s.', 'advanced-sidebar-menu' ),
				type?.labels?.name ?? '',
				type?.labels?.singular_name.toLowerCase() ?? '',
				sprintf( '<a href="https://onpointplugins.com/advanced-sidebar-menu/advanced-sidebar-menu-pro-widget-docs/advanced-sidebar-menu-pro-excluding-menu-items/#%s"  target="_blank">', 'advanced-sidebar-menu/categories' === block ? 'categories' : 'pages' ),
				'</a>'
			),
		}} />;


	return (
		<PanelBody
			/* translators: Selected post type plural label */
			title={sprintf( __( '%s to Exclude', 'advanced-sidebar-menu' ), type?.labels?.name ?? '' )}
		>
			{'' !== error &&
				<Notice className={styles.error} status={'error'} isDismissible={false}>
					{error}
				</Notice>}

			<div className={styles.search}>
				<TextControl
					ref={inputRef}
					type={'search'}
					/* translators: Selected post type plural label */
					placeholder={sprintf( __( 'Search %s', 'advanced-sidebar-menu' ), type?.labels?.name.toLowerCase() ?? '' )}
					value={search}
					onChange={ev => setSearch( ev )}
					onKeyDown={ev => {
						if ( 0 === items.length ) {
							return;
						}
						// Focus the first item when the down arrow or tab is pressed.
						if ( 'ArrowDown' === ev.key || 'Tab' === ev.key ) {
							firstItemRef.current?.focus();
							ev.preventDefault();
						}
					}}
				/>

				{loading && <Spinner />}

				{items.length > 0 &&
					<Popover
						focusOnMount={false}
						placement={'bottom'}
						onClose={clearItems}
						expandOnMobile={false}
						offset={6}
					>
						<NavigableMenu orientation={'vertical'}>
							<MenuGroup
								className={classNames( styles.items, {
									'advanced-sidebar-menu-exclude-field-items': true,
									[ styles.loading ]: loading,
								} )}
							>
								{items.map( ( item, i ) => (
									<MenuItem
										className={styles.item}
										key={`${item.id}-${type?.slug}`}
										ref={0 === i ? firstItemRef : undefined}
										icon={isPage ? 'text-page' : 'category'}
										role={'option'}
										onClick={() => {
											selectItem( item );
											inputRef.current?.focus();
										}}
									>
										<TextHighlight
											text={decodeEntities( item.title )}
											highlight={search}
										/>
									</MenuItem>
								) )}
							</MenuGroup>
						</NavigableMenu>
					</Popover>}
			</div>


			<div className={styles.selected}>
				{selected.map( item => (
					<Button
						isSmall
						isPrimary
						icon={'no-alt'}
						key={`remove-${item.id}-${type?.slug}`}
						onClick={() => {
							const existing = getIdsFromValue( attributes.exclude ?? '' );
							setAttributes( {
								exclude: existing.filter( i => i !== item.id ).join( ',' ),
							} );
							setSelected( selected.filter( i => i.id !== item.id ) );
						}}
					>
						{decodeEntities( item.title )}
					</Button>
				) )}
			</div>

			<p
				className={styles.help}>
				{help}
			</p>
		</PanelBody>
	);
};

export default ExcludeField;
